import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import DOMPurify from 'dompurify';
import { logEvent } from 'firebase/analytics';

import analytics from '@/routes/firebase';
import NavData from '@/config/data/nav/nav_data.json';
import useContent from '@/localization/data_export';
import { episodeList, episodeDetail as episodeFetch, programDetail as programFetch } from '@/network/apis/apiUri';

export default function TvProgramVod({ location, match }) {
  const queryClient = useQueryClient();

  const anchorTitle = useContent(['global', 'anchor', 'title']);
  const mode = useSelector((state) => state.mode);

  const search = new URLSearchParams(location.search);

  const programId = match.params.programCode;

  const episodeId = search.get('id');
  const boardId = search.get('board');

  const listRef = useRef(null);

  const [toggle, setToggle] = useState(false);

  const { data: episodeDetail, isSuccess } = useQuery({
    queryKey: ['tv', 'program', 'episode', episodeId, mode],
    queryFn: async () => {
      const response = await episodeFetch({ episode_id: episodeId, hit: true });

      const result = {
        title: response.data.title.find((v) => v.lan_code === mode)?.text,
        mediaKey: response.data.media_info.media_content_key,
        image: response.data.image.url,
        content: DOMPurify.sanitize(response.data.content.find((v) => v.lan_code === mode)?.text ?? ''),
        broadcastedAt: response.data.broadcast_date.slice(0, 10),
      };

      return result;
    },
    enabled: !!episodeId && !!mode,
  });

  const { data: programDetail } = useQuery({
    queryKey: ['tv', 'program', programId, mode],
    queryFn: async () => {
      const response = await programFetch({ program_id: programId });

      return {
        title: response.data.title.find((v) => v.lan_code === mode)?.text,
        image: response.data.image.url,
      };
    },
    enabled: !!programId && !!mode,
  });

  const {
    data: videoList,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['tv', 'program', 'episode', 'side-list', mode],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await episodeList({
        lan_code: mode,
        board_id: boardId,
        type: 'episode',
        page_info: {
          row_count: 10,
          number: pageParam,
        },
      });

      return {
        items: response.data.item,
        currentPage: pageParam,
        totalPage: response.data.paging_info.total_page_count,
      };
    },
    getNextPageParam: (last) => {
      if (last.currentPage >= last.totalPage) return undefined;
      return last.currentPage + 1;
    },
    enabled: !!mode && !!boardId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const ulElment = listRef.current;

    if (!ulElment) return;

    const el = ulElment.querySelector('li:last-child');

    if (!el) return;

    const observe = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        if (!hasNextPage) return observe.disconnect();
        fetchNextPage();
        observe.unobserve(el);
      }
    });
    observe.observe(el);
    return () => {
      observe.disconnect();
    };
  }, [videoList, hasNextPage, fetchNextPage]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries(['tv', 'program', 'vod', 'side-list']);
    };
  }, []);

  return (
    <main className={`tv-program-episode-detail${toggle ? '--list-off' : ''}`}>
      <article className="tv-program-episode-detail__article">
        {isSuccess && (
          <>
            <figure className="tv-program-episode-detail__article-player">
              {episodeDetail.mediaKey ? (
                <iframe
                  src={`https://v.kr.kollus.com/${episodeDetail.mediaKey}?enable_pip=true`}
                  title="news player"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                ></iframe>
              ) : (
                <img
                  src={`${episodeDetail.image}`}
                  alt={episodeDetail.title}
                  className="list_img"
                  style={{ width: '100%' }}
                  onError={(e) => (e.target.src = require('@/assets/images/common/672_379_A.png').default)}
                />
              )}
            </figure>
            <section className="tv-program-episode-detail__article-date">
              <time className="time-fs-fixed">{`${episodeDetail?.broadcastedAt} KST`}</time>
              <button type="button" onClick={() => setToggle(!toggle)}>
                {toggle ? 'List on' : 'List off'}
              </button>
            </section>
            <h2 className="tv-program-episode-detail__article-title">{episodeDetail?.title}</h2>
            <div className="tv-program-episode-detail__article-content">
              <p dangerouslySetInnerHTML={{ __html: episodeDetail?.content }} />
              <Link to={`${NavData['TV_MAIN'].path}/${programId}`} title={anchorTitle}>
                {programDetail?.title || episodeDetail.title}
              </Link>
            </div>
          </>
        )}
      </article>
      <section className="tv-program-episode-detail__side-latest">
        <h2>Episode</h2>
        <hr />
        <ul ref={listRef} className="style-scroll">
          {videoList &&
            videoList.pages
              .flatMap((page) => page.items)
              .map((item) => (
                <li key={item.episode_id} data-active={+episodeId === item.episode_id}>
                  <Link
                    to={`${NavData['TV_MAIN'].path}/${programId}/archive?id=${item.episode_id}&board=${boardId}`}
                    className="item"
                    title={anchorTitle}
                    onClick={() =>
                      logEvent(analytics, 'select_item', {
                        item_list_name: `${item.title}`,
                        item_list_id: `episode_${item.episode_id}`,
                        items: [
                          {
                            item_id: `${item.episode_id}`,
                            item_name: `${item.title}`,
                            item_brand: 'episode',
                          },
                        ],
                      })
                    }
                  >
                    <img
                      src={item.image_url}
                      alt=""
                      onError={(e) => (e.target.src = require('@/assets/images/common/672_379_A.png').default)}
                    />
                    <h3>{item.title}</h3>
                    <time className="time-fs-fixed">
                      {item?.broadcast_date ? item?.broadcast_date?.slice(0, 10) : ''}
                    </time>
                  </Link>
                </li>
              ))}
        </ul>
      </section>
    </main>
  );
}
